"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { CheckCircle2, ChevronRight, CircleDashed, Cpu, Timer, Trophy, UserRound } from "lucide-react";
import { cn } from "@/lib/utils";
import { hasHtmlContent } from "@/lib/html-content";
import { formatNumber } from "@/lib/format-datetime";
import { formatMemoryLimitMb, formatTimeLimit } from "../lib/format";
import { DifficultyTag } from "./DifficultyTag";
import { ratingBand, ratingTextClass } from "../lib/rating-band";
import { topicName } from "../lib/topics";
import type { JudgeProblemView } from "../types";
import { TopicPill } from "./TopicPill";
import { RunTwiceTag } from "./RunTwiceTag";
import { InteractiveTag } from "./InteractiveTag";
import { ProblemRatingChip } from "./ProblemRatingChip";

/**
 * The practice problem's head, `EX-PUB21` (the Problem board of the "Judge
 * pages" proposal): where you are, the problem in both its titles, what it
 * asks of a solution, and on the right where it came from, how often you have
 * tried it and which language the statement reads in.
 *
 * It sits at the top of the statement pane rather than across the whole split:
 * on a phone the panes stack under a draggable divider, and a head over both
 * would take the height the editor needs. The statement panel below it renders
 * bare and takes its language from here.
 *
 * `EX-CRD01` (ADR-0098): the row under the title opens with the problem's
 * author, a person and their username in their rating band's colour, linked
 * to their profile, the way Toph credits a setter. A problem made from the
 * admin panel arrives with no author and the row starts at the difficulty.
 * `EX-PRT01` puts the rating from the round beside the difficulty.
 */
export function ProblemHead({
  problem,
  locale,
  lang,
  onLangChange,
  submissionCount,
}: {
  problem: JudgeProblemView;
  locale: string;
  lang: "en" | "bn";
  onLangChange: (lang: "en" | "bn") => void;
  /** The reader's own submissions on this problem; `null` when there is no reader to count for. */
  submissionCount: number | null;
}) {
  const t = useTranslations("judge.problem");
  const tTopics = useTranslations("judge.topics");
  const tBands = useTranslations("judge.ratingBands");
  const tTwice = useTranslations("judge.runTwice");
  // `EX-RTW03`, board D: the tag, and the time limit said per run.
  const runTwice = problem.kind === "run_twice";
  const author = problem.author ?? null;
  const authorHandle = author ? (author.usernameDisplay ?? author.username ?? author.name) : null;
  const hasBn = hasHtmlContent(problem.statement_bn) || !!problem.title_bn;
  const useBn = lang === "bn" && !!problem.title_bn;
  const title = useBn ? (problem.title_bn ?? problem.title_en) : problem.title_en;
  const altTitle = problem.title_bn ? (useBn ? problem.title_en : problem.title_bn) : null;
  const round = problem.credit?.round ?? null;
  const roundTitle = round ? (locale === "bn" && round.title_bn ? round.title_bn : round.title_en) : null;
  const solvers = problem.solvers ?? null;

  return (
    <div className="border-b border-neutral-100 pb-5">
      <nav aria-label={t("crumbLabel")} className="flex min-w-0 items-center gap-1.5 text-xs text-neutral-500">
        <Link
          href={`/${locale}/judge/problems`}
          className="shrink-0 font-semibold transition-colors duration-200 hover:text-neutral-900"
        >
          {t("crumbLibrary")}
        </Link>
        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-neutral-300" aria-hidden />
        <span className="truncate text-neutral-700" aria-current="page">
          {title}
        </span>
      </nav>

      <div className="mt-3 flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {problem.solvedState === "solved" ? (
              <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
            ) : problem.solvedState === "attempted" ? (
              <CircleDashed className="h-5 w-5 shrink-0 text-amber-500" aria-hidden />
            ) : null}
            <h1 className="text-2xl font-bold tracking-tight text-neutral-900 sm:text-[28px]">{title}</h1>
            {altTitle ? <span className="text-base text-neutral-400">{altTitle}</span> : null}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            {author && authorHandle ? (
              <span className="inline-flex items-center gap-1.5">
                <UserRound className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden />
                {author.username ? (
                  <Link
                    href={`/${locale}/u/${author.username}`}
                    aria-label={t("authorLabel", { name: authorHandle })}
                    title={author.rating !== null ? tBands(ratingBand(author.rating)) : undefined}
                    className={cn(
                      "font-semibold underline-offset-2 transition-colors duration-200 hover:underline",
                      ratingTextClass(author.rating) ?? "text-neutral-700",
                    )}
                  >
                    {authorHandle}
                  </Link>
                ) : (
                  <span className="font-semibold text-neutral-700">{authorHandle}</span>
                )}
              </span>
            ) : null}
            <DifficultyTag difficulty={problem.difficulty} label={t(`difficulty_${problem.difficulty}`)} />
            {/* `EX-PRT01`: the rating from the round, beside the setter's difficulty. */}
            {problem.rating ? <ProblemRatingChip rating={problem.rating} locale={locale} focusable /> : null}
            {runTwice ? <RunTwiceTag /> : null}
            {problem.kind === "interactive" ? <InteractiveTag /> : null}
            <span className="inline-flex items-center gap-1.5 text-neutral-500">
              <Timer className="h-3.5 w-3.5" aria-hidden />
              {runTwice
                ? tTwice("timePerRun", { seconds: formatTimeLimit(problem.limits.timeMs) })
                : t("timeLimitValue", { seconds: formatTimeLimit(problem.limits.timeMs) })}
            </span>
            <span className="inline-flex items-center gap-1.5 text-neutral-500">
              <Cpu className="h-3.5 w-3.5" aria-hidden />
              {t("memoryLimitValue", { mb: formatMemoryLimitMb(problem.limits.memoryKb) })}
            </span>
            {problem.tags.map((tag) => (
              <TopicPill key={tag} tag={tag} name={topicName(tTopics, tag)} locale={locale} />
            ))}
            {solvers ? (
              <span className="text-neutral-500">{t("solvers", { count: formatNumber(solvers, locale) })}</span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {round && roundTitle ? (
            <Link
              href={`/${locale}/contests/${round.slug}`}
              aria-label={round.letter ? t("fromRound", { round: roundTitle, letter: round.letter }) : roundTitle}
              className="inline-flex h-9 max-w-[260px] items-center gap-2 rounded-[9px] border border-neutral-200 px-3 text-[13px] text-neutral-600 transition-colors duration-200 hover:border-primary/40 hover:text-neutral-900"
            >
              <Trophy className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
              <span className="truncate">{roundTitle}</span>
              {round.letter ? <span className="shrink-0">· {round.letter}</span> : null}
            </Link>
          ) : null}
          {submissionCount !== null && submissionCount > 0 ? (
            <a
              href="#my-submissions"
              className="inline-flex h-9 items-center gap-2 rounded-[9px] border border-neutral-200 px-3 text-[13px] text-neutral-600 transition-colors duration-200 hover:border-primary/40 hover:text-neutral-900"
            >
              {t("mySubmissions")}
              <span className="font-mono text-xs text-neutral-400">{formatNumber(submissionCount, locale)}</span>
            </a>
          ) : null}
          {hasBn ? (
            <div role="group" aria-label={t("statementLanguage")} className="inline-flex rounded-[10px] bg-neutral-100 p-1">
              {(["en", "bn"] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={lang === value}
                  onClick={() => onLangChange(value)}
                  className={cn(
                    "h-8 rounded-[8px] px-3 text-[13px] font-medium transition-colors duration-200",
                    lang === value
                      ? "bg-white text-neutral-900 shadow-sm ring-1 ring-neutral-200"
                      : "text-neutral-600 hover:text-neutral-900",
                  )}
                >
                  {value === "en" ? "English" : "বাংলা"}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
